import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkles, Send, Bot, User, RefreshCw, Trash2 } from "lucide-react";

const SESSION_KEY = "ai_admin_session_id";

const PROVIDER_MODELS = {
  openai:    ["gpt-5.6-terra", "gpt-5.6-luna", "gpt-5.6-sol", "gpt-5.6"],
  anthropic: ["claude-sonnet-5-5", "claude-sonnet-5", "claude-opus-5-5", "claude-haiku-4-5-20251001"],
  gemini:    ["gemini-3.1-pro-preview", "gemini-3-flash-preview", "gemini-3.5-flash", "gemini-2.5-pro"],
};

const SUGGESTIONS = [
  "Quali sono le migliori 3 campagne per riattivare clienti dormienti?",
  "Suggeriscimi 5 idee di premi per il periodo estivo",
  "Scrivi un messaggio WhatsApp di ritorno per clienti inattivi da 60 giorni",
  "Come strutturare un cluster VIP-notturni? Da quali metriche partire?",
];

export default function AiAssistant() {
  const [sessionId, setSessionId] = useState(() => {
    let s = typeof localStorage !== "undefined" ? localStorage.getItem(SESSION_KEY) : null;
    if (!s) { s = Math.random().toString(36).slice(2, 12); if (typeof localStorage !== "undefined") localStorage.setItem(SESSION_KEY, s); }
    return s;
  });
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-5.6-terra");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { provider, model, session_id: sessionId, message });
      setMessages((prev) => [...prev, { role: "assistant", content: data.text || "" }]);
    } catch (err) {
      toast.error(formatApiError(err));
      setMessages((prev) => [...prev, { role: "assistant", content: "⚠️ Errore: " + formatApiError(err) }]);
    } finally { setSending(false); }
  };

  const resetSession = () => {
    const s = Math.random().toString(36).slice(2, 12);
    setSessionId(s);
    if (typeof localStorage !== "undefined") localStorage.setItem(SESSION_KEY, s);
    setMessages([]);
    toast.success("Nuova sessione avviata");
  };

  return (
    <div className="space-y-4 animate-fade-up" data-testid="ai-assistant-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-red"><Sparkles className="h-3 w-3" /> AI ASSISTANT</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Copilota Admin</h1>
          <p className="text-zinc-400 mt-1">
            Chiedi consigli operativi sulla loyalty, idee campagne, copy, analisi cluster. Scegli il modello che preferisci.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={provider} onValueChange={(v) => { setProvider(v); setModel(PROVIDER_MODELS[v][0]); }}>
            <SelectTrigger data-testid="ai-assistant-provider" className="bg-zinc-950 border-zinc-800 w-full sm:w-40 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="openai">OpenAI GPT-5.6</SelectItem>
              <SelectItem value="anthropic">Claude</SelectItem>
              <SelectItem value="gemini">Gemini</SelectItem>
            </SelectContent>
          </Select>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="ai-assistant-model" className="bg-zinc-950 border-zinc-800 w-full sm:w-44 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROVIDER_MODELS[provider].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={resetSession}
            data-testid="ai-assistant-reset" className="border-zinc-800"
            title="Reset chat">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="card-elev p-0 overflow-hidden">
        <div ref={listRef} className="h-[60vh] overflow-y-auto p-4 space-y-3" data-testid="ai-assistant-messages">
          {messages.length === 0 && (
            <div className="text-center text-zinc-500 py-10">
              <Bot className="h-8 w-8 mx-auto mb-2 opacity-60" />
              <p className="text-sm mb-3">Ciao! Come posso aiutarti oggi?</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => send(s)}
                    data-testid={`ai-assistant-suggest-${i}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`ai-msg-${i}`}>
              {m.role === "assistant" && <div className="h-8 w-8 rounded-full bg-fuchsia-500/20 text-fuchsia-300 flex items-center justify-center shrink-0"><Bot className="h-4 w-4" /></div>}
              <div className={`px-3 py-2 rounded-2xl max-w-[75%] whitespace-pre-wrap text-sm ${
                m.role === "user" ? "bg-fuchsia-600 text-white" : "bg-zinc-900 border border-zinc-800 text-zinc-100"
              }`}>{m.content}</div>
              {m.role === "user" && <div className="h-8 w-8 rounded-full bg-zinc-800 text-zinc-300 flex items-center justify-center shrink-0"><User className="h-4 w-4" /></div>}
            </div>
          ))}
          {sending && (
            <div className="flex gap-2" data-testid="ai-assistant-typing">
              <div className="h-8 w-8 rounded-full bg-fuchsia-500/20 text-fuchsia-300 flex items-center justify-center"><Bot className="h-4 w-4" /></div>
              <div className="px-3 py-2 rounded-2xl bg-zinc-900 border border-zinc-800 text-zinc-400 text-sm">
                <RefreshCw className="h-4 w-4 animate-spin inline mr-2" /> Sto pensando…
              </div>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-zinc-800 bg-zinc-950">
          <div className="flex gap-2">
            <Textarea
              data-testid="ai-assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Scrivi qui… (Invio per inviare, Shift+Invio nuova riga)"
              rows={2}
              className="bg-zinc-900 border-zinc-800 resize-none"
            />
            <Button data-testid="ai-assistant-send" onClick={() => send()} disabled={sending || !input.trim()}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 self-end h-10">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
